// OIDC PKCE flow for OpenGov SSO (login.ogintegration.us / Auth0-compatible)
// No library — uses Web Crypto API directly.

const CLIENT_ID = 'jXsyeL5AO8xGMEIGveQlwfwEQQzlnTxq';
const DOMAIN    = 'https://login.ogintegration.us';
const AUDIENCE  = 'opengov';
const SCOPE     = 'openid profile email';

function redirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

// ── PKCE helpers ──────────────────────────────────────────────────

function randomBase64url(len = 32): string {
  const buf = new Uint8Array(len);
  crypto.getRandomValues(buf);
  return btoa(String.fromCharCode(...buf))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256Base64url(plain: string): Promise<string> {
  const bytes = new TextEncoder().encode(plain);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ── Login redirect ────────────────────────────────────────────────

export async function startOidcLogin(): Promise<void> {
  const verifier   = randomBase64url(32);
  const challenge  = await sha256Base64url(verifier);
  const state      = randomBase64url(16);

  sessionStorage.setItem('oidc_verifier', verifier);
  sessionStorage.setItem('oidc_state',    state);

  const params = new URLSearchParams({
    response_type:         'code',
    client_id:             CLIENT_ID,
    redirect_uri:          redirectUri(),
    scope:                 SCOPE,
    audience:              AUDIENCE,
    code_challenge:        challenge,
    code_challenge_method: 'S256',
    state,
  });

  window.location.href = `${DOMAIN}/authorize?${params}`;
}

// ── Callback / token exchange ─────────────────────────────────────

export interface OidcTokens {
  accessToken:   string;
  idToken:       string;
  refreshToken?: string;
  expiresIn:     number;
}

export async function handleOidcCallback(): Promise<OidcTokens> {
  const params = new URLSearchParams(window.location.search);
  const error  = params.get('error');
  if (error) throw new Error(params.get('error_description') ?? error);

  const code = params.get('code');
  if (!code) throw new Error('No authorization code in callback URL');

  const returnedState = params.get('state');
  const savedState    = sessionStorage.getItem('oidc_state');
  if (returnedState !== savedState) throw new Error('OAuth state mismatch — possible CSRF');

  const verifier = sessionStorage.getItem('oidc_verifier');
  if (!verifier) throw new Error('No PKCE verifier found — please try signing in again');

  sessionStorage.removeItem('oidc_verifier');
  sessionStorage.removeItem('oidc_state');

  const res = await fetch(`${DOMAIN}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type:    'authorization_code',
      client_id:     CLIENT_ID,
      code,
      code_verifier: verifier,
      redirect_uri:  redirectUri(),
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error_description?: string };
    throw new Error(err.error_description ?? `Token exchange failed (${res.status})`);
  }

  const data = await res.json() as {
    access_token:  string;
    id_token:      string;
    refresh_token?: string;
    expires_in:    number;
  };

  return {
    accessToken:  data.access_token,
    idToken:      data.id_token,
    refreshToken: data.refresh_token,
    expiresIn:    data.expires_in,
  };
}

// ── ID token decoder (no signature validation — server validates) ──

export interface IdTokenClaims {
  sub:         string;
  email:       string;
  name?:       string;
  given_name?: string;
  family_name?:string;
  role?:       string;
}

export function parseIdToken(idToken: string): IdTokenClaims {
  try {
    const part    = idToken.split('.')[1];
    const padded  = part.replace(/-/g, '+').replace(/_/g, '/');
    const json    = atob(padded);
    return JSON.parse(json) as IdTokenClaims;
  } catch {
    throw new Error('Failed to parse ID token');
  }
}
